import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { authConfig } from '../config/auth.config';
import { isPublicAppPath } from '../guards/public-path';

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  token_type: string;
  scope?: string;
}

export interface UserInfo {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  companyId?: number;
  company_id?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  
  // Configuración de Keycloak
  private readonly keycloakUrl = authConfig.keycloakUrl;
  private readonly clientId = authConfig.clientId;
  private readonly clientSecret = authConfig.clientSecret;
  /** Solo para login: URI de callback donde Keycloak redirige tras autorizar (ej. origin + '/callback') */
  private readonly redirectUri = authConfig.redirectUri;
  /** Solo para logout: URL a la que redirigir al usuario después de cerrar sesión en Keycloak */
  private readonly logoutRedirectUri = authConfig.logoutRedirectUri;
  private readonly scopes = authConfig.scopes;

  /** Margen antes del vencimiento para refrescar el access token en silencio. */
  private readonly TOKEN_EXPIRY_WARNING_MS = 1 * 60 * 1000;
  /** Revisión periódica por si el navegador atrasa timers en pestañas inactivas. */
  private readonly TOKEN_EXPIRY_POLL_MS = 30_000;
  /** Si el callback falla otra vez dentro de esta ventana tras reiniciar el login, se muestra el error en lugar de reintentar. */
  private readonly CALLBACK_RETRY_WINDOW_MS = 15_000;
  private readonly CALLBACK_RETRY_AT_KEY = 'oauth_callback_retry_at';

  private readonly callbackErrorState = signal<string | null>(null);
  /** Error del callback OAuth que no se pudo recuperar reiniciando el login. */
  readonly callbackError = this.callbackErrorState.asReadonly();

  private tokenExpiryTimerId: ReturnType<typeof setTimeout> | null = null;
  private tokenExpiryIntervalId: ReturnType<typeof setInterval> | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private pageShowHandler: ((event: PageTransitionEvent) => void) | null = null;
  private sessionExpiryHandled = false;
  private tokenRefreshInFlight = false;
  
  // Estado de autenticación (signals para que la UI reaccione al logout / expiración)
  private accessToken = signal<string | null>(this.getStoredToken());
  private tokenExpiryMs = signal<number | null>(this.getStoredTokenExpiry());
  private tokenSubject = new BehaviorSubject<string | null>(this.accessToken());
  public token$ = this.tokenSubject.asObservable();

  /** Avatar del API (`/users/me`); tiene prioridad sobre Keycloak `picture`. */
  private profileAvatarUrl = signal<string | null>(null);
  readonly profileAvatarUrlSignal = this.profileAvatarUrl.asReadonly();

  public isAuthenticated = computed(() => {
    const token = this.accessToken();
    const expiry = this.tokenExpiryMs();
    if (!token || !expiry) return false;
    return Date.now() < expiry;
  });

  constructor() {
    // Verificar si hay un código de autorización en la URL (callback)
    this.handleCallback();
    this.startTokenExpiryWatcher();
  }

  /**
   * Inicia el flujo de autenticación redirigiendo a Keycloak
   * Guarda la ruta actual para volver después del login.
   */
  login(returnUrl?: string): void {
    const state = this.generateRandomString(32);
    
    // Guardar state para verificación
    sessionStorage.setItem('oauth_state', state);
    if (returnUrl) {
      sessionStorage.setItem('oauth_return_url', returnUrl);
    }
    
    // Flujo básico sin PKCE (si Keycloak requiere PKCE, necesitarás configurarlo)
    const authUrl = `${this.keycloakUrl}/protocol/openid-connect/auth?` +
      `client_id=${encodeURIComponent(this.clientId)}&` +
      `redirect_uri=${encodeURIComponent(this.redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent(this.scopes)}&` +
      `state=${state}`;
    
    window.location.href = authUrl;
  }

  /**
   * Maneja el callback de Keycloak después de la autenticación
   */
  private handleCallback(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (!error && !code && !state) {
      // Recarga de /callback sin parámetros (ya consumidos): no hay nada que intercambiar.
      if (this.isOAuthCallbackPath()) {
        this.leaveCallbackWithoutParams();
      }
      return;
    }

    console.log('Manejando callback de Keycloak', { code: !!code, state, error });

    if (error) {
      // p. ej. Keycloak devuelve `temporarily_unavailable` / `authentication_expired`
      // cuando la pantalla de login quedó abierta más tiempo que la sesión de autenticación.
      const errorDescription = urlParams.get('error_description');
      console.error('Error de autenticación:', error, errorDescription);
      this.restartLoginAfterCallbackFailure(
        `Error de autenticación: ${errorDescription ?? error}`
      );
      return;
    }
    
    if (code && state) {
      const storedState = sessionStorage.getItem('oauth_state');
      console.log('Verificando state:', { received: state, stored: storedState });
      
      if (state !== storedState) {
        console.error('State mismatch - posible ataque CSRF');
        this.restartLoginAfterCallbackFailure('No se pudo validar la respuesta de inicio de sesión.');
        return;
      }
      
      console.log('State verificado correctamente, intercambiando código por token...');
      
      // Intercambiar código por token
      this.exchangeCodeForToken(code).subscribe({
        next: (tokenResponse) => {
          console.log('Token recibido, guardando...');
          this.setToken(tokenResponse);
          const returnUrl = sessionStorage.getItem('oauth_return_url');
          this.cleanupCallback();
          sessionStorage.removeItem(this.CALLBACK_RETRY_AT_KEY);
          console.log('Redirigiendo a la ruta original...');
          // Redirigir a la ruta original si existe
          window.location.href = returnUrl ? `${window.location.origin}${returnUrl}` : window.location.origin;
        },
        error: (err) => {
          console.error('Error al obtener token:', err);
          this.restartLoginAfterCallbackFailure('No se pudo completar el inicio de sesión.');
        }
      });
    } else {
      console.warn('OAuth: faltan code o state en la URL (respuesta incompleta)');
      this.restartLoginAfterCallbackFailure('Respuesta de inicio de sesión incompleta.');
    }
  }

  private isOAuthCallbackPath(): boolean {
    return window.location.pathname === '/callback';
  }

  /** En /callback sin code/state: ir a la app si hay sesión vigente, si no, iniciar login. */
  private leaveCallbackWithoutParams(): void {
    if (this.hasValidSession()) {
      window.location.replace(window.location.origin);
      return;
    }
    this.clearToken();
    this.login();
  }

  /**
   * Reinicia el flujo de login conservando la ruta de retorno. Si ya se reintentó hace muy poco
   * (Keycloak vuelve a fallar de inmediato), muestra el error para no entrar en un ciclo de redirecciones.
   */
  private restartLoginAfterCallbackFailure(message: string): void {
    const returnUrl = sessionStorage.getItem('oauth_return_url') ?? undefined;
    this.cleanupCallback();

    const lastRetryAt = Number(sessionStorage.getItem(this.CALLBACK_RETRY_AT_KEY) ?? '0');
    if (Date.now() - lastRetryAt < this.CALLBACK_RETRY_WINDOW_MS) {
      sessionStorage.removeItem(this.CALLBACK_RETRY_AT_KEY);
      this.callbackErrorState.set(message);
      return;
    }

    sessionStorage.setItem(this.CALLBACK_RETRY_AT_KEY, Date.now().toString());
    this.clearToken();
    this.login(returnUrl);
  }

  /**
   * Intercambia el código de autorización por un token de acceso
   * Llama directamente a Keycloak
   */
  private exchangeCodeForToken(code: string): Observable<TokenResponse> {
    let params = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', this.clientId)
      .set('code', code)
      .set('redirect_uri', this.redirectUri);
    
    // Si hay client_secret, agregarlo (para clientes "confidential")
    if (this.clientSecret) {
      params = params.set('client_secret', this.clientSecret);
    }
    
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/x-www-form-urlencoded'
    };
    
    console.log('Intercambiando código por token directamente con Keycloak...', {
      url: `${this.keycloakUrl}/protocol/openid-connect/token`,
      clientId: this.clientId,
      redirectUri: this.redirectUri
    });
    
    return this.http.post<TokenResponse>(
      `${this.keycloakUrl}/protocol/openid-connect/token`,
      params.toString(),
      { headers }
    ).pipe(
      tap(response => {
        console.log('Token recibido exitosamente');
      }),
      catchError(error => {
        console.error('Error al intercambiar código por token:', error);
        console.error('Detalles del error:', {
          status: error.status,
          statusText: error.statusText,
          error: error.error,
          message: error.message,
          url: error.url
        });
        
        // Mostrar mensaje más específico según el error
        if (error.status === 400) {
          console.error('Error 400: Verifica que el código no haya expirado y que el redirect_uri coincida exactamente');
        } else if (error.status === 401) {
          console.error('Error 401: Verifica el client_id y client_secret (si es necesario)');
        } else if (error.status === 0 || error.status === undefined) {
          console.error('Error de CORS: Verifica que Keycloak tenga configurado CORS para permitir peticiones desde ' + window.location.origin);
          console.error('En Keycloak, ve a: Cliente -> pento-app-client -> Settings -> Web Origins y agrega: ' + window.location.origin);
        }
        
        throw error;
      })
    );
  }

  /**
   * Obtiene información del usuario autenticado
   */
  getUserInfo(): Observable<UserInfo> {
    const token = this.getToken();
    if (!token) {
      return of();
    }
    
    return this.http.get<UserInfo>(
      `${this.keycloakUrl}/protocol/openid-connect/userinfo`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    ).pipe(
      catchError(err => {
        console.error('Error al obtener información del usuario:', err);
        return of();
      })
    );
  }

  /**
   * Refresca el token de acceso usando el refresh token.
   * Llama directamente a Keycloak.
   */
  refreshToken(): Observable<TokenResponse> {
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No hay refresh token disponible'));
    }

    let params = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('client_id', this.clientId)
      .set('refresh_token', refreshToken);

    if (this.clientSecret) {
      params = params.set('client_secret', this.clientSecret);
    }

    return this.http.post<TokenResponse>(
      `${this.keycloakUrl}/protocol/openid-connect/token`,
      params.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    ).pipe(
      tap(tokenResponse => this.setToken(tokenResponse))
    );
  }

  /**
   * Indica si el access token actual sigue vigente (según expiry local).
   * Útil ante HTTP 401: si el token no ha vencido, suele ser un tema de privilegios, no de sesión.
   */
  isAccessTokenValid(): boolean {
    this.syncTokenFromStorage();
    return this.hasValidSession();
  }

  /**
   * Intenta renovar el access token con el refresh token.
   * Solo redirige a Keycloak si no hay refresh token o Keycloak rechaza el refresh.
   */
  private tryRefreshSession(): void {
    if (this.tokenRefreshInFlight || this.sessionExpiryHandled || this.isOnAuthOrPublicRoute()) {
      return;
    }

    if (!this.getStoredRefreshToken()) {
      this.handleSessionExpired();
      return;
    }

    this.tokenRefreshInFlight = true;
    this.clearTokenExpiryTimer();

    this.refreshToken().subscribe({
      next: () => {
        this.tokenRefreshInFlight = false;
        this.sessionExpiryHandled = false;
      },
      error: (err) => {
        console.error('Error al refrescar token; se requiere re-login:', err);
        this.tokenRefreshInFlight = false;
        this.handleSessionExpired();
      }
    });
  }

  /**
   * URL de Keycloak para recuperación de contraseña (flujo "olvidé mi contraseña").
   * Abrir en nueva pestaña para que el usuario restablezca su contraseña.
   */
  getResetCredentialsUrl(): string {
    const redirectUri = this.redirectUri ?? window.location.origin + '/callback';
    return `${this.keycloakUrl}/protocol/openid-connect/auth?` +
      `client_id=${encodeURIComponent(this.clientId)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&scope=openid&` +
      `kc_action=reset_credentials`;
  }

  /**
   * Cierra la sesión y redirige a Keycloak para logout
   */
  logout(): void {
    this.stopTokenExpiryWatcher();
    this.clearToken();
    
    // Logout en Keycloak: post_logout_redirect_uri = a dónde ir DESPUÉS del logout (no es la URI del login)
    const postLogoutRedirect = this.logoutRedirectUri ?? window.location.origin;
    const logoutUrl = `${this.keycloakUrl}/protocol/openid-connect/logout?` +
      `client_id=${encodeURIComponent(this.clientId)}&` +
      `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirect)}`;
    
    window.location.href = logoutUrl;
  }

  /**
   * Limpia la sesión local y redirige a Keycloak login.
   * Usado cuando el token falta, está por vencer o la API responde 401.
   */
  handleSessionExpired(returnUrl?: string): void {
    if (this.sessionExpiryHandled || this.isOnAuthOrPublicRoute()) {
      return;
    }

    this.sessionExpiryHandled = true;
    // Solo pausar timers; conservar pageshow/visibility para detectar “atrás” / bfcache
    this.clearTokenExpiryTimer();
    if (this.tokenExpiryIntervalId) {
      clearInterval(this.tokenExpiryIntervalId);
      this.tokenExpiryIntervalId = null;
    }

    const fallbackReturnUrl =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '/';

    this.clearToken();
    this.login(returnUrl ?? fallbackReturnUrl);
  }

  /**
   * Obtiene el token de acceso actual
   */
  getToken(): string | null {
    return this.accessToken();
  }

  /**
   * Roles de realm del access token (`realm_access.roles`), p. ej. SALE_READ.
   */
  getRealmRoles(): string[] {
    const claims = this.getAccessTokenClaims();
    const roles = claims?.realm_access?.roles;
    return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string') : [];
  }

  hasRole(role: string): boolean {
    return this.getRealmRoles().includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    if (roles.length === 0) return true;
    const granted = new Set(this.getRealmRoles());
    return roles.some((role) => granted.has(role));
  }

  hasAllRoles(roles: string[]): boolean {
    if (roles.length === 0) return true;
    const granted = new Set(this.getRealmRoles());
    return roles.every((role) => granted.has(role));
  }

  private getAccessTokenClaims(): { realm_access?: { roles?: unknown } } | null {
    const token = this.getToken() ?? this.getStoredToken();
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      return JSON.parse(this.decodeBase64Url(payload)) as { realm_access?: { roles?: unknown } };
    } catch {
      return null;
    }
  }

  /**
   * Establece el token y lo guarda en localStorage
   */
  private setToken(tokenResponse: TokenResponse): void {
    const expiry = Date.now() + (tokenResponse.expires_in * 1000);
    
    localStorage.setItem('access_token', tokenResponse.access_token);
    localStorage.setItem('token_expiry', expiry.toString());
    
    if (tokenResponse.refresh_token) {
      localStorage.setItem('refresh_token', tokenResponse.refresh_token);
    }
    if (tokenResponse.id_token) {
      localStorage.setItem('id_token', tokenResponse.id_token);
    }
    
    this.applySessionState(tokenResponse.access_token, expiry);
    this.sessionExpiryHandled = false;
    this.scheduleTokenExpiryRedirect();
  }

  /**
   * Limpia el token del almacenamiento
   */
  private clearToken(): void {
    this.clearTokenExpiryTimer();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('id_token');
    this.profileAvatarUrl.set(null);
    this.applySessionState(null, null);
  }

  private applySessionState(token: string | null, expiry: number | null): void {
    this.accessToken.set(token);
    this.tokenExpiryMs.set(expiry);
    this.tokenSubject.next(token);
  }

  /** Alinea memoria con localStorage (p. ej. tras bfcache / otra pestaña). */
  private syncTokenFromStorage(): void {
    const token = this.getStoredToken();
    const expiry = this.getStoredTokenExpiry();
    if (token !== this.accessToken() || expiry !== this.tokenExpiryMs()) {
      this.applySessionState(token, expiry);
    }
  }

  private hasValidSession(): boolean {
    const token = this.accessToken();
    const expiry = this.tokenExpiryMs();
    return !!token && !!expiry && Date.now() < expiry;
  }

  /**
   * Revisa sesión al reanudar pestaña/historial. Si no hay token válido en ruta privada → login.
   */
  ensureAuthenticatedSession(): void {
    if (this.isOnAuthOrPublicRoute()) {
      return;
    }

    this.syncTokenFromStorage();

    if (this.hasValidSession()) {
      this.sessionExpiryHandled = false;
      // Reanudar vigilancia si se restauró la página desde bfcache tras un logout forzado
      if (!this.tokenExpiryIntervalId) {
        this.tokenExpiryIntervalId = setInterval(
          () => this.checkTokenExpiry(),
          this.TOKEN_EXPIRY_POLL_MS
        );
      }
      this.scheduleTokenExpiryRedirect();
      return;
    }

    // Tras “atrás” el flag puede quedar en true y bloquear un nuevo login
    this.sessionExpiryHandled = false;
    this.handleSessionExpired();
  }

  /**
   * Supervisa la vigencia del access token y lo refresca ~1 min antes de vencer.
   */
  private startTokenExpiryWatcher(): void {
    this.scheduleTokenExpiryRedirect();

    if (this.tokenExpiryIntervalId) {
      clearInterval(this.tokenExpiryIntervalId);
    }
    this.tokenExpiryIntervalId = setInterval(
      () => this.checkTokenExpiry(),
      this.TOKEN_EXPIRY_POLL_MS
    );

    if (typeof document !== 'undefined' && !this.visibilityChangeHandler) {
      this.visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible') {
          this.ensureAuthenticatedSession();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    if (typeof window !== 'undefined' && !this.pageShowHandler) {
      this.pageShowHandler = () => {
        // Incluye restauración desde bfcache (atrás tras ir a Keycloak)
        this.ensureAuthenticatedSession();
      };
      window.addEventListener('pageshow', this.pageShowHandler);
    }
  }

  private stopTokenExpiryWatcher(): void {
    this.clearTokenExpiryTimer();

    if (this.tokenExpiryIntervalId) {
      clearInterval(this.tokenExpiryIntervalId);
      this.tokenExpiryIntervalId = null;
    }

    if (typeof document !== 'undefined' && this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }

    if (typeof window !== 'undefined' && this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler);
      this.pageShowHandler = null;
    }
  }

  private clearTokenExpiryTimer(): void {
    if (this.tokenExpiryTimerId) {
      clearTimeout(this.tokenExpiryTimerId);
      this.tokenExpiryTimerId = null;
    }
  }

  private scheduleTokenExpiryRedirect(): void {
    this.clearTokenExpiryTimer();

    const token = this.accessToken();
    const expiry = this.tokenExpiryMs();
    if (!token || !expiry || this.isOnAuthOrPublicRoute()) {
      return;
    }

    const msUntilRefresh = expiry - Date.now() - this.TOKEN_EXPIRY_WARNING_MS;
    if (msUntilRefresh <= 0) {
      this.tryRefreshSession();
      return;
    }

    this.tokenExpiryTimerId = setTimeout(
      () => this.tryRefreshSession(),
      msUntilRefresh
    );
  }

  private checkTokenExpiry(): void {
    if (this.isOnAuthOrPublicRoute() || this.tokenRefreshInFlight) {
      return;
    }

    this.syncTokenFromStorage();

    if (!this.hasValidSession()) {
      // Sesión local vencida: intentar refresh antes de botar al login
      this.sessionExpiryHandled = false;
      this.tryRefreshSession();
      return;
    }

    const expiry = this.tokenExpiryMs();
    if (expiry != null && Date.now() >= expiry - this.TOKEN_EXPIRY_WARNING_MS) {
      this.tryRefreshSession();
    }
  }

  private isOnAuthOrPublicRoute(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return isPublicAppPath(window.location.pathname || '');
  }

  /**
   * Obtiene el token almacenado
   */
  private getStoredToken(): string | null {
    return localStorage.getItem('access_token');
  }

  private getStoredIdToken(): string | null {
    return localStorage.getItem('id_token');
  }

  getIdTokenClaims(): UserInfo | null {
    const idToken = this.getStoredIdToken();
    if (!idToken) return null;
    try {
      const payload = idToken.split('.')[1];
      if (!payload) return null;
      const decoded = this.decodeBase64Url(payload);
      return JSON.parse(decoded) as UserInfo;
    } catch (error) {
      console.error('Error al decodificar id_token:', error);
      return null;
    }
  }

  getUserPicture(): string | null {
    return this.profileAvatarUrl() || this.getIdTokenClaims()?.picture || null;
  }

  setProfileAvatarUrl(url: string | null | undefined): void {
    this.profileAvatarUrl.set(url?.trim() ? url : null);
  }

  /**
   * Obtiene el companyId del token JWT (Keycloak).
   * Busca en access_token y, si no está, en id_token.
   * Acepta claim "companyId" o "company_id" como número, string o array de un elemento (formato Keycloak).
   */
  getCompanyId(): number | null {
    const token = this.getToken() ?? this.getStoredToken();
    const fromAccess = this.getClaimFromToken(token, 'companyId', 'company_id');
    if (fromAccess != null) return fromAccess;
    const idClaims = this.getIdTokenClaims();
    if (idClaims) {
      const id = idClaims.companyId ?? idClaims.company_id;
      return this.parseCompanyId(id);
    }
    return null;
  }

  private getClaimFromToken(token: string | null, ...keys: string[]): number | null {
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = this.decodeBase64Url(payload);
      const claims = JSON.parse(decoded) as Record<string, unknown>;
      for (const key of keys) {
        const value = claims[key];
        const num = this.parseCompanyId(value);
        if (num != null) return num;
      }
      return null;
    } catch {
      return null;
    }
  }

  private parseCompanyId(value: unknown): number | null {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') return Number.isNaN(value) ? null : value;
    if (Array.isArray(value) && value.length > 0) return this.parseCompanyId(value[0]);
    const num = parseInt(String(value), 10);
    return Number.isNaN(num) ? null : num;
  }

  getUserDisplayName(): string | null {
    const claims = this.getIdTokenClaims();
    return claims?.name || claims?.preferred_username || claims?.email || null;
  }

  /**
   * Obtiene el refresh token almacenado
   */
  private getStoredRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Obtiene la fecha de expiración del token
   */
  private getStoredTokenExpiry(): number | null {
    const expiry = localStorage.getItem('token_expiry');
    return expiry ? parseInt(expiry, 10) : null;
  }

  private decodeBase64Url(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return atob(padded);
  }

  /**
   * Limpia los parámetros de callback de la URL
   */
  private cleanupCallback(): void {
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_return_url');
    
    // Limpiar URL sin recargar la página
    if (window.location.search.includes('code=') || window.location.search.includes('state=')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  /**
   * Genera una cadena aleatoria para state
   */
  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const values = new Uint8Array(length);
    crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }
    return result;
  }
}
